export const parseLocaleNumber = (str: string | undefined | null) => {
  // Remove any spaces, normalize Unicode minus sign (U+2212) to hyphen-minus, and trim
  const cleaned = str?.replace(/\s/g, '')?.replace('\u2212', '-')?.trim();

  if (!cleaned) return NaN;

  // Check if the string contains only valid numeric characters and separators
  const validNumberPattern = /^[+-]?[0-9,.]+$/;
  if (!validNumberPattern.test(cleaned)) {
    return NaN;
  }

  // Count occurrences of separators
  const commaCount = (cleaned.match(/,/g) || []).length;
  const periodCount = (cleaned.match(/\./g) || []).length;

  // Find the last occurrence of comma or period
  const lastCommaIndex = cleaned.lastIndexOf(',');
  const lastPeriodIndex = cleaned.lastIndexOf('.');

  let result;

  if (lastCommaIndex === -1 && lastPeriodIndex === -1) {
    // No separators - just a whole number
    result = cleaned;
  } else if (lastCommaIndex !== -1 && lastPeriodIndex !== -1) {
    // Both separators present - need to validate format before processing
    if (lastCommaIndex > lastPeriodIndex) {
      // Might be European format (1.500,50) - validate structure
      // All periods should come before the last comma and form valid thousand separators
      const beforeLastComma = cleaned.substring(0, lastCommaIndex);
      const afterLastComma = cleaned.substring(lastCommaIndex + 1);

      // Check if there are any commas before the last comma (invalid pattern like "12,34.56,78")
      if (beforeLastComma.includes(',')) {
        return NaN;
      }

      // Decimal part should be ≤ 3 digits
      if (afterLastComma.length > 3) {
        return NaN;
      }

      // Validate thousand separator structure if there are periods
      if (periodCount > 0) {
        const periodSegments = beforeLastComma.split('.');
        // Check that middle segments are exactly 3 digits (proper thousand separators)
        for (let i = 1; i < periodSegments.length; i++) {
          if (periodSegments[i].length !== 3) {
            return NaN;
          }
        }
      }

      // European format - remove thousand separators and convert decimal comma
      result = cleaned
        .replace(/\./g, '') // Remove thousand separators
        .replace(',', '.'); // Convert decimal comma to period
    } else {
      // Might be US format (1,500.50) - validate structure
      // All commas should come before the last period and form valid thousand separators
      const beforeLastPeriod = cleaned.substring(0, lastPeriodIndex);
      const afterLastPeriod = cleaned.substring(lastPeriodIndex + 1);

      // Check if there are any periods before the last period (invalid pattern like "12.34,56.78")
      if (beforeLastPeriod.includes('.')) {
        return NaN;
      }

      // Decimal part should be ≤ 3 digits
      if (afterLastPeriod.length > 3) {
        return NaN;
      }

      // Validate thousand separator structure if there are commas
      if (commaCount > 0) {
        const commaSegments = beforeLastPeriod.split(',');
        // Check that middle segments are exactly 3 digits (proper thousand separators)
        for (let i = 1; i < commaSegments.length; i++) {
          if (commaSegments[i].length !== 3) {
            return NaN;
          }
        }
      }

      // US format - remove thousand separators
      result = cleaned.replace(/,/g, ''); // Remove thousand separators
    }
  } else if (commaCount > 1 && periodCount === 0) {
    // Multiple commas, no periods - European format with thousand separators
    // But check if it's actually invalid (like "123,45,67")
    const lastCommaIndex = cleaned.lastIndexOf(',');
    const afterLastComma = cleaned.substring(lastCommaIndex + 1);

    // If the part after the last comma has more than 3 digits, it's invalid
    if (afterLastComma.length > 3) {
      return NaN;
    }

    // Check for invalid patterns like consecutive short segments
    const segments = cleaned.split(',');
    for (let i = 1; i < segments.length - 1; i++) {
      // Middle segments (thousand separators) should be exactly 3 digits
      if (segments[i].length !== 3) {
        return NaN;
      }
    }

    // Last comma should be decimal separator
    result = `${cleaned.substring(0, lastCommaIndex).replace(/,/g, '')}.${cleaned.substring(lastCommaIndex + 1)}`;
  } else if (periodCount > 1 && commaCount === 0) {
    // Multiple periods, no commas - US format with thousand separators
    // But check if it's actually invalid (like "123.45.67")
    const lastPeriodIndex = cleaned.lastIndexOf('.');
    const afterLastPeriod = cleaned.substring(lastPeriodIndex + 1);

    // If the part after the last period has more than 3 digits, it's invalid
    if (afterLastPeriod.length > 3) {
      return NaN;
    }

    // Check for invalid patterns like consecutive short segments
    const segments = cleaned.split('.');
    for (let i = 1; i < segments.length - 1; i++) {
      // Middle segments (thousand separators) should be exactly 3 digits
      if (segments[i].length !== 3) {
        return NaN;
      }
    }

    // Last period should be decimal separator
    result = `${cleaned.substring(0, lastPeriodIndex).replace(/\./g, '')}.${cleaned.substring(lastPeriodIndex + 1)}`;
  } else {
    // Only one separator - determine if it's decimal or thousand
    const singleSeparator = lastCommaIndex !== -1 ? ',' : '.';
    const parts = cleaned.split(singleSeparator);

    if (parts[1] && parts[1].length <= 2) {
      // 1-2 digits after separator - likely decimal (e.g., "1,50" or "1.5")
      result = `${parts[0]}.${parts[1]}`;
    } else if (parts[1] && parts[1].length === 3) {
      // Exactly 3 digits after separator - ambiguous case
      // Use heuristic: if it looks like a "round" number (ends in trailing 0), treat as thousand
      if (parts[1].endsWith('0')) {
        result = parts.join(''); // thousand separator
      } else {
        result = `${parts[0]}.${parts[1]}`; // decimal
      }
    } else {
      // More than 3 digits after separator - definitely decimal
      result = `${parts[0]}.${parts[1]}`;
    }
  }

  const parsed = parseFloat(result);

  if (parsed === 0) {
    // this ensures that -0 gets returned as 0
    return 0;
  }

  // Final validation to ensure we have a valid number
  return isNaN(parsed) ? NaN : parsed;
};
